import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { TextLink } from "@/components/Buttons";
import { TEACHER_LIBRARY, OWNER_LIBRARY } from "@/data/offer";

const ProductCard = ({ p, i, idPrefix }) => (
  <motion.article
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-30px" }}
    transition={{ duration: 0.5, delay: (i % 5) * 0.05, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`${idPrefix}-product-card-${i}`}
    className="product-card group flex flex-col border border-ink/12 bg-white"
  >
    <div className="relative aspect-[4/3] overflow-hidden border-b border-ink/10 bg-alabaster">
      <img
        src={p.img}
        alt={`Okładka: ${p.name}`}
        loading="lazy"
        className="h-full w-full object-contain p-3 transition-transform duration-700 ease-out group-hover:scale-[1.05]"
      />
      <span className="absolute left-2 top-2 bg-white px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-ink/70">{p.type}</span>
    </div>
    <div className="flex flex-1 flex-col p-4">
      <h4 className="font-display text-[17px] font-medium leading-tight text-ink">{p.name}</h4>
      <p className="mt-1.5 flex-1 text-[12.5px] leading-snug text-ink/60">{p.desc}</p>
      <TextLink href={p.url} testId={`${idPrefix}-product-details-${i}`} className="mt-3 !text-[12px]">
        Sprawdź szczegóły
      </TextLink>
    </div>
  </motion.article>
);

const Group = ({ g, gi, idPrefix }) => (
  <div>
    <div className="flex flex-wrap items-end justify-between gap-3 border-t border-ink/15 pt-5">
      <h3 className="font-display text-2xl font-medium leading-tight text-ink sm:text-3xl">
        <span className="mr-3 text-sm text-red">0{gi + 1}</span>
        {g.category}
      </h3>
      <p className="text-[13px] text-ink/55 sm:max-w-[48ch] sm:text-right">{g.intro}</p>
    </div>
    <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {g.items.map((p, i) => (
        <ProductCard key={p.name} p={p} i={gi * 10 + i} idPrefix={idPrefix} />
      ))}
    </div>
  </div>
);

const CONFIG = {
  teacher: {
    id: "co-dostajesz",
    lib: TEACHER_LIBRARY,
    eyebrow: "Co dostajesz w abonamencie nauczycielskim",
    title: "Poznaj swoją bibliotekę nauczycielską",
    intro: "W abonamencie masz wszystkie kursy i e-booki w jednym miejscu, pogrupowane tak, żebyś szybko znalazła to, czego dziś potrzebujesz.",
    bg: "bg-white",
    preview: false,
  },
  owner: {
    id: "biblioteka-wlascicielska",
    lib: OWNER_LIBRARY,
    eyebrow: "Co dostajesz w abonamencie właścicielskim",
    title: "Poznaj swoją bibliotekę właścicielską",
    intro: "Materiały o prowadzeniu szkoły jako organizacji: od planu na rok, przez sekretariat i procesy, po współpracę z firmami.",
    bg: "bg-alabaster",
    preview: false,
  },
};

export const Library = ({ kind = "teacher" }) => {
  const c = CONFIG[kind];
  const [open, setOpen] = useState(false);
  const stripRef = useRef(null);
  const items = c.lib.flatMap((g) => g.items.map((p) => ({ ...p, category: g.category })));
  const total = items.length;
  const slide = (dir) => stripRef.current?.scrollBy({ left: dir * Math.round(stripRef.current.clientWidth * 0.8), behavior: "smooth" });

  return (
    <section id={c.id} data-testid={`library-section-${kind}`} className={`${c.bg} py-20 sm:py-28`}>
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <Reveal className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Eyebrow>{c.eyebrow}</Eyebrow>
            <h2 className="font-display mt-5 text-4xl font-medium leading-[1.02] tracking-[-0.02em] text-ink sm:text-5xl lg:text-6xl">
              {c.title}
            </h2>
          </div>
          <div className="self-end lg:col-span-5">
            <p className="text-base leading-relaxed text-ink/65 sm:text-lg">{c.intro}</p>
            <p data-testid={`library-count-${kind}`} className="mt-4 inline-flex items-center gap-3 bg-red px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.16em] text-white">
              {total} kursów i e-booków
            </p>
          </div>
        </Reveal>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-ink/15 pt-5">
          <button
            type="button"
            data-testid={`library-toggle-${kind}`}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="group inline-flex items-center gap-3 border border-ink px-6 py-3.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
          >
            {open ? "Zwiń bibliotekę" : "Rozwiń całą bibliotekę"}
            <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          </button>
          {!open && (
            <div className="flex gap-2">
              <button type="button" aria-label="Przewiń w lewo" data-testid={`library-prev-${kind}`} onClick={() => slide(-1)} className="flex h-10 w-10 items-center justify-center border border-ink/20 text-ink transition-colors hover:border-ink hover:bg-ink hover:text-white">
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button type="button" aria-label="Przewiń w prawo" data-testid={`library-next-${kind}`} onClick={() => slide(1)} className="flex h-10 w-10 items-center justify-center border border-ink/20 text-ink transition-colors hover:border-ink hover:bg-ink hover:text-white">
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {open ? (
          <motion.div
            key="grid"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid={`library-grid-${kind}`}
            className="mx-auto mt-8 max-w-[1440px] space-y-10 px-5 sm:px-8 lg:px-12"
          >
            {c.lib.map((g, gi) => (
              <Group key={g.category} g={g} gi={gi} idPrefix={kind} />
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="strip"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            ref={stripRef}
            data-testid={`library-strip-${kind}`}
            style={{ paddingLeft: "max(1.25rem, calc((100vw - 1440px) / 2 + 3rem))", paddingRight: "max(1.25rem, calc((100vw - 1440px) / 2 + 3rem))", scrollPaddingLeft: "max(1.25rem, calc((100vw - 1440px) / 2 + 3rem))" }}
            className="library-strip mt-6 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-4"
          >
            {items.map((p, i) => (
              <a
                key={p.name}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${kind}-strip-card-${i}`}
                className="product-card group flex w-[150px] shrink-0 snap-start flex-col border border-ink/12 bg-white sm:w-[170px]"
              >
                <div className="relative aspect-square overflow-hidden border-b border-ink/10 bg-alabaster">
                  <img
                    src={p.img}
                    alt={`Okładka: ${p.name}`}
                    loading="lazy"
                    className="h-full w-full object-contain p-2 transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                  />
                </div>
                <div className="p-3">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-red">{p.type}</p>
                  <h3 className="font-display mt-1 text-[15px] font-medium leading-tight text-ink group-hover:text-red">{p.name}</h3>
                </div>
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
